import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

// Removed FiBrain (does not exist in standard react-icons/fi), replaced with FiCompass
const { 
  FiThumbsUp, FiCheckCircle, FiXCircle, FiRefreshCw, FiStar, FiAward, 
  FiCompass, FiBook, FiHeart, FiTarget, FiZap, FiClock, FiUsers, FiMic, FiSun,
  FiCoffee, FiSmile, FiGlobe, FiEye
} = FiIcons;

const Reflection = ({ updateProgress, setCurrentSection }) => {
  const [currentQuiz, setCurrentQuiz] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [selectedReflection, setSelectedReflection] = useState(null);
  const [reflectionTexts, setReflectionTexts] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [score, setScore] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);
  const [timeSpent, setTimeSpent] = useState(0);
  const [startTime] = useState(Date.now());

  useEffect(() => {
    setCurrentSection('reflection');
    updateProgress('reflection');
    const timer = setInterval(() => {
      setTimeSpent(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const quizQuestions = [
    // 1. Collective Identity
    {
      id: 'collective-thinking',
      question: 'What does using "እኛ" (we) instead of "እኔ" (I) show in Ethiopian culture?',
      options: [
        { id: 'a', text: 'Confusion about grammar', icon: FiXCircle },
        { id: 'b', text: 'Humility and community spirit', icon: FiHeart },
        { id: 'c', text: 'Formal education', icon: FiBook },
        { id: 'd', text: 'Religious belief', icon: FiUsers }
      ],
      correct: 'b',
      explanation: 'Using "we" (እኛ) instead of "I" (እኔ) shows humility and emphasizes community over individual importance in Ethiopian culture.',
      culturalContext: 'This reflects the Ethiopian value of collective identity where group harmony takes precedence over individual recognition.',
      difficulty: 'Beginner'
    },
    // 2. Respect/Hierarchy
    {
      id: 'respect-forms',
      question: 'When should you use "እርስዎ" (erswo) instead of "አንተ" (ante)?',
      options: [
        { id: 'a', text: 'When talking to friends', icon: FiUsers },
        { id: 'b', text: 'When talking to children', icon: FiHeart },
        { id: 'c', text: 'When showing respect to elders', icon: FiAward },
        { id: 'd', text: 'When you are angry', icon: FiXCircle }
      ],
      correct: 'c',
      explanation: '"እርስዎ" (erswo) is the formal, respectful form used with elders, teachers, and authority figures.',
      culturalContext: 'Formal address shows proper upbringing and maintains social harmony in hierarchical relationships.',
      difficulty: 'Intermediate'
    },
    // 3. Conflict Resolution
    {
      id: 'arbitration-language',
      question: 'In Ethiopian arbitration (Shimgilina), why is collective language important?',
      options: [
        { id: 'a', text: 'It sounds more educated', icon: FiBook },
        { id: 'b', text: 'It emphasizes community consensus', icon: FiUsers },
        { id: 'c', text: 'It is required by law', icon: FiTarget },
        { id: 'd', text: 'It makes decisions faster', icon: FiZap }
      ],
      correct: 'b',
      explanation: 'Collective language reflects the community-based approach to conflict resolution in Ethiopian culture.',
      culturalContext: 'Shimgilina emphasizes that conflicts affect the whole community, not just individuals.',
      difficulty: 'Advanced'
    },
    // 4. Grammar & Values
    {
      id: 'grammar-culture',
      question: 'How does Amharic grammar reflect social relationships?',
      options: [
        { id: 'a', text: 'Through complex verb conjugations', icon: FiBook },
        { id: 'b', text: 'Through pronoun choices and formality', icon: FiUsers },
        { id: 'c', text: 'Through word order changes', icon: FiTarget },
        { id: 'd', text: 'Through vocabulary selection only', icon: FiHeart }
      ],
      correct: 'b',
      explanation: 'Pronoun choices and formality levels directly show respect, hierarchy, and social awareness.',
      culturalContext: 'Grammar becomes a social tool that maintains cultural values and proper relationships.',
      difficulty: 'Intermediate'
    },
    // 5. Non-Verbal Communication (Body Language)
    {
      id: 'eye-contact',
      question: 'In traditional Ethiopian culture, what does lowering your eyes when speaking to an elder signify?',
      options: [
        { id: 'a', text: 'Dishonesty or hiding something', icon: FiXCircle },
        { id: 'b', text: 'Deep respect and humility', icon: FiAward },
        { id: 'c', text: 'Boredom with the conversation', icon: FiClock },
        { id: 'd', text: 'Sadness or grief', icon: FiHeart }
      ],
      correct: 'b',
      explanation: 'Unlike in some Western cultures where eye contact equals confidence, lowering one\'s gaze to an elder is a sign of deep respect.',
      culturalContext: 'Respect is shown not just through words (like "Erswo"), but through body language that lowers the self relative to the elder.',
      difficulty: 'Advanced'
    },
    // 6. Hospitality (Gursha)
    {
      id: 'gursha',
      question: 'What is the meaning behind "Gursha" (feeding someone a bite of food with your hand)?',
      options: [
        { id: 'a', text: 'Helping someone who cannot eat', icon: FiUsers },
        { id: 'b', text: 'Love, friendship, and intimacy', icon: FiHeart },
        { id: 'c', text: 'Checking if food is hot', icon: FiZap },
        { id: 'd', text: 'Finishing food quickly', icon: FiClock }
      ],
      correct: 'b',
      explanation: 'Gursha is a gesture of love. Refusing a gursha can be seen as rejecting friendship.',
      culturalContext: 'Food in Ethiopia is rarely eaten alone; it is a communal act that bonds people together.',
      difficulty: 'Beginner'
    },
    // 7. The Concept of "Eshi"
    {
      id: 'eshi-meaning',
      question: 'The word "Eshi" (እሺ) is used constantly. What is its deeper social meaning?',
      options: [
        { id: 'a', text: 'Just "Yes"', icon: FiCheckCircle },
        { id: 'b', text: 'Social harmony and agreeableness', icon: FiSmile },
        { id: 'c', text: 'Submission to authority', icon: FiTarget },
        { id: 'd', text: 'It has no meaning', icon: FiXCircle }
      ],
      correct: 'b',
      explanation: '"Eshi" means okay, alright, I agree, or I am listening. It is the oil that keeps social interactions smooth.',
      culturalContext: 'Ethiopians value social harmony highly. Saying "Eshi" shows you are willing to cooperate and maintain peace.',
      difficulty: 'Intermediate'
    },
    // 8. Perception of Time
    {
      id: 'ethiopian-time',
      question: 'In traditional Ethiopian time, when does the 12-hour clock cycle begin?',
      options: [
        { id: 'a', text: 'At Midnight (12:00 AM)', icon: FiClock },
        { id: 'b', text: 'At Sunrise (approx. 6:00 AM)', icon: FiSun },
        { id: 'c', text: 'At Noon (12:00 PM)', icon: FiSun },
        { id: 'd', text: 'At Sunset (approx. 6:00 PM)', icon: FiClock }
      ],
      correct: 'b',
      explanation: 'The Ethiopian day starts at sunrise. So, 7:00 AM international time is "1 o\'clock" (And seat) in Ethiopian time.',
      culturalContext: 'This connects time directly to the natural cycle of the sun rather than an abstract mechanical clock.',
      difficulty: 'Advanced'
    },
    // 9. Coffee Ceremony (Buna)
    {
      id: 'coffee-ceremony',
      question: 'The Coffee Ceremony involves 3 rounds (Abol, Tona, Bereka). What does sitting through all 3 teach?',
      options: [
        { id: 'a', text: 'How to drink more caffeine', icon: FiCoffee },
        { id: 'b', text: 'Patience and the value of conversation', icon: FiUsers },
        { id: 'c', text: 'Cooking skills', icon: FiZap },
        { id: 'd', text: 'Hierarchy of seating', icon: FiAward }
      ],
      correct: 'b',
      explanation: 'The ceremony is slow by design. It forces people to slow down, talk, and connect deeply over an extended period.',
      culturalContext: 'In a fast-paced world, the Buna ceremony preserves the space for slow, meaningful human connection.',
      difficulty: 'Intermediate'
    },
    // 10. Greetings & Peace
    {
      id: 'selam-meaning',
      question: 'The most common greeting is "Selam". What does it literally translate to?',
      options: [
        { id: 'a', text: 'Hello', icon: FiSmile },
        { id: 'b', text: 'Peace', icon: FiGlobe },
        { id: 'c', text: 'Welcome', icon: FiHeart },
        { id: 'd', text: 'Good day', icon: FiSun }
      ],
      correct: 'b',
      explanation: 'While used as "Hello", "Selam" literally means "Peace". You are wishing peace upon the other person.',
      culturalContext: 'Peace (Selam) is a central pillar of Ethiopian social values, reflected in the very first word spoken to a stranger.',
      difficulty: 'Beginner'
    }
  ];

  const reflectionPrompts = [
    {
      id: 'individual-collective',
      title: 'Individual vs. Collective Thinking',
      icon: FiUsers,
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      prompt: 'Compare how Ethiopian culture uses "we" (እኛ) versus Western emphasis on "I". How might this affect problem-solving and decision-making in your own life?',
      questions: [
        'When do you prefer to think individually versus collectively?',
        'How could using "we" more often change your relationships?',
        'What situations work better with collective thinking?'
      ],
      example: 'Instead of "I solved this problem," try "We solved this problem together."'
    },
    {
      id: 'respect-language',
      title: 'Respect in Language',
      icon: FiHeart,
      color: 'from-rose-500 to-pink-500',
      bgColor: 'bg-rose-50',
      borderColor: 'border-rose-200',
      prompt: 'Think about how using formal pronouns (እርስዎ) shows respect. How does this compare to respect-showing in other languages or cultures you know?',
      questions: [
        'How do you show respect in your daily interactions?',
        'What words or phrases do you use to show respect?',
        'How does language affect how you feel about others?'
      ],
      example: 'Notice how using "sir" or "ma\'am" changes the tone of a conversation.'
    },
    {
      id: 'grammar-culture-values',
      title: 'Grammar and Cultural Values',
      icon: FiBook,
      color: 'from-purple-500 to-indigo-500',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      prompt: 'What have you learned about how grammar rules reflect cultural values? Give specific examples from Amharic.',
      questions: [
        'Which Amharic grammar rule surprised you most?',
        'How does Amharic grammar show what Ethiopians value?',
        'Could your language be changed to better reflect your values?'
      ],
      example: 'The use of "our father" for priests shows collective reverence.'
    }
  ];

  const handleQuizAnswer = (questionId, answerId) => {
    const newAnswers = { ...quizAnswers, [questionId]: answerId };
    setQuizAnswers(newAnswers);
  };

  const calculateScore = () => {
    let correct = 0;
    quizQuestions.forEach((question) => {
      if (quizAnswers[question.id] === question.correct) {
        correct++;
      }
    });
    return correct;
  };

  const submitQuiz = () => {
    const finalScore = calculateScore();
    setScore(finalScore);
    setShowResults(true);
    if (finalScore >= quizQuestions.length * 0.8) {
      setShowCelebration(true);
      setTimeout(() => setShowCelebration(false), 3000);
    }
  };

  const resetQuiz = () => {
    setQuizAnswers({});
    setShowResults(false);
    setScore(0);
    setCurrentQuiz(0);
  };

  const handleReflectionSubmit = async (reflectionId) => {
    setIsSubmitting(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsSubmitting(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'text-green-600 bg-green-100';
      case 'Intermediate': return 'text-yellow-600 bg-yellow-100';
      case 'Advanced': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const QuizCard = ({ question, index, total }) => {
    const userAnswer = quizAnswers[question.id];
    const isCorrect = userAnswer === question.correct;
    const showFeedback = showResults && userAnswer;

    return (
      <div className={`bg-white rounded-2xl shadow-lg border-2 transition-all duration-300 ${showFeedback ? (isCorrect ? 'border-green-500' : 'border-red-500') : 'border-gray-200'} overflow-hidden`}>
        {/* Question Header */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-6 border-b border-gray-200">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-bold text-lg">
                {index + 1}
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-800 mb-2">{question.question}</h3>
                <div className="flex items-center gap-3">
                  <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${getDifficultyColor(question.difficulty)}`}>
                    {question.difficulty}
                  </span>
                  {showFeedback && (
                    <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${isCorrect ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {isCorrect ? (
                        <>
                          <SafeIcon icon={FiCheckCircle} /> Correct
                        </>
                      ) : (
                        <>
                          <SafeIcon icon={FiXCircle} /> Incorrect
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Options */}
        <div className="p-6">
          <div className="space-y-3">
            {question.options.map((option) => {
              const isSelected = userAnswer === option.id;
              const isCorrectOption = option.id === question.correct;
              const showOptionFeedback = showFeedback && (isSelected || isCorrectOption);

              return (
                <button
                  key={option.id}
                  onClick={() => !showResults && handleQuizAnswer(question.id, option.id)}
                  disabled={showResults}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-300 ${showFeedback
                      ? isCorrectOption
                        ? 'border-green-500 bg-green-50'
                        : isSelected && !isCorrectOption
                          ? 'border-red-500 bg-red-50'
                          : 'border-gray-200 bg-gray-50'
                      : isSelected
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    } ${!showResults && 'cursor-pointer'} `}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${showFeedback
                        ? isCorrectOption
                          ? 'border-green-500 bg-green-500'
                          : isSelected
                            ? 'border-red-500 bg-red-500'
                            : 'border-gray-300'
                        : isSelected
                          ? 'border-purple-500 bg-purple-500'
                          : 'border-gray-300'
                      } `}>
                      {showFeedback ? (
                        isCorrectOption ? (
                          <SafeIcon icon={FiCheckCircle} className="text-white text-sm" />
                        ) : isSelected ? (
                          <SafeIcon icon={FiXCircle} className="text-white text-sm" />
                        ) : null
                      ) : isSelected ? (
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      ) : null}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <SafeIcon icon={option.icon} className="text-gray-400" />
                        <span className="text-gray-700">{option.text}</span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Feedback Section */}
        {showFeedback && (
          <div className={`p-6 border-t ${isCorrect ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <div className="flex items-start gap-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isCorrect ? 'bg-green-500' : 'bg-red-500'}`}>
                {isCorrect ? (
                  <SafeIcon icon={FiCheckCircle} className="text-white" />
                ) : (
                  <SafeIcon icon={FiXCircle} className="text-white" />
                )}
              </div>
              <div className="flex-1">
                <h4 className={`font-bold mb-2 ${isCorrect ? 'text-green-800' : 'text-red-800'}`}>
                  {isCorrect ? 'Excellent! You got it right!' : 'Not quite, but great effort!'}
                </h4>
                <p className="text-gray-700 mb-3 leading-relaxed">{question.explanation}</p>
                <div className={`p-3 rounded-lg text-sm ${isCorrect ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                  <SafeIcon icon={FiBook} className="inline mr-2" />
                  <strong>Cultural Context:</strong> {question.culturalContext}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const ReflectionCard = ({ reflection }) => (
    <div className={`bg-white rounded-2xl shadow-lg border-2 border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-xl flex flex-col h-full ${selectedReflection === reflection.id ? 'ring-2 ring-purple-500 transform scale-105' : ''}`}>
      {/* Header */}
      <div className={`bg-gradient-to-r ${reflection.color} p-6`}>
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/30 flex-shrink-0">
            <SafeIcon icon={reflection.icon} className="text-3xl text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-2xl font-bold text-white mb-2">{reflection.title}</h3>
            <p className="text-white/90 leading-relaxed text-sm">{reflection.prompt}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 flex flex-col flex-1">
        {/* Guiding Questions */}
        <div className="mb-6 flex-1">
          <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
            <SafeIcon icon={FiTarget} className="text-purple-500" /> Guiding Questions
          </h4>
          <div className="space-y-2">
            {reflection.questions.map((question, idx) => (
              <div key={idx} className="flex items-start gap-2">
                <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center text-xs font-bold text-purple-600 mt-0.5 flex-shrink-0">
                  {idx + 1}
                </div>
                <p className="text-gray-600 text-sm">{question}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Example */}
        <div className={`${reflection.bgColor} rounded-lg p-4 border ${reflection.borderColor} mb-6`}>
          <div className="flex items-start gap-2">
            <SafeIcon icon={FiSun} className="text-purple-500 text-sm mt-0.5 flex-shrink-0" />
            <div>
              <h5 className="font-medium text-gray-800 mb-1">Example</h5>
              <p className="text-sm text-gray-600">{reflection.example}</p>
            </div>
          </div>
        </div>

        {/* Reflection Input */}
        <div className="mt-auto">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Share Your Thoughts
          </label>
          <textarea
            className="w-full p-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
            rows="4"
            placeholder="Reflect on your experiences and insights..."
            value={reflectionTexts[reflection.id] || ''}
            onChange={(e) => setReflectionTexts({ ...reflectionTexts, [reflection.id]: e.target.value })}
          />
          <div className="flex justify-between items-center mt-3">
            <span className="text-sm text-gray-500">
              {reflectionTexts[reflection.id]?.length || 0} characters
            </span>
            <button
              onClick={() => handleReflectionSubmit(reflection.id)}
              disabled={isSubmitting || !reflectionTexts[reflection.id]}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${isSubmitting || !reflectionTexts[reflection.id]
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-purple-500 text-white hover:bg-purple-600'
                } `}
            >
              {isSubmitting ? 'Saving...' : 'Save Reflection'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (showResults && score >= quizQuestions.length * 0.8) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-amber-50 to-orange-50 p-4 md:p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 text-center relative overflow-hidden">
            {/* Background Decoration */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-yellow-200 to-orange-200 rounded-full opacity-20 transform translate-x-32 -translate-y-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-br from-amber-200 to-yellow-200 rounded-full opacity-20 transform -translate-x-24 translate-y-24"></div>

            <div className="relative z-10">
              {/* Trophy Icon */}
              <div className="mb-8">
                <div className="w-24 h-24 mx-auto bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center shadow-2xl transform hover:scale-110 transition-transform">
                  <SafeIcon icon={FiAward} className="text-5xl text-white" />
                </div>
              </div>

              {/* Title */}
              <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                🎉 Outstanding!
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                You've mastered the Social Grammar of Amharic!
              </p>

              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-2xl p-6 border border-yellow-200">
                  <SafeIcon icon={FiStar} className="text-3xl text-yellow-500 mb-2" />
                  <div className="text-3xl font-bold text-gray-800">{score}/{quizQuestions.length}</div>
                  <div className="text-sm text-gray-500">Final Score</div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-6 border border-purple-200">
                  <SafeIcon icon={FiCompass} className="text-3xl text-purple-500 mb-2" />
                  <div className="text-3xl font-bold text-gray-800">{Math.round((score/quizQuestions.length)*100)}%</div>
                  <div className="text-sm text-gray-500">Mastery Level</div>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200">
                  <SafeIcon icon={FiClock} className="text-3xl text-green-500 mb-2" />
                  <div className="text-3xl font-bold text-gray-800">{formatTime(timeSpent)}</div>
                  <div className="text-sm text-gray-500">Time Spent</div>
                </div>
              </div>

              {/* Achievement Badge */}
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-6 text-white mb-8">
                <div className="flex items-center justify-center gap-3 mb-3">
                  <SafeIcon icon={FiAward} className="text-3xl text-yellow-400" />
                  <h3 className="text-2xl font-bold">Amharic Cultural Ambassador</h3>
                </div>
                <p className="text-purple-100">
                  You've demonstrated exceptional understanding of Ethiopian cultural values and social dynamics through language.
                </p>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => { setShowResults(false); resetQuiz(); }}
                  className="px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-xl font-bold hover:shadow-lg transition-all"
                >
                  <SafeIcon icon={FiRefreshCw} className="mr-2" /> Try Again
                </button>
                <Link
                  to="/audio"
                  className="px-6 py-3 bg-white border-2 border-purple-500 text-purple-600 rounded-xl font-bold hover:bg-purple-50 transition-all"
                >
                  Review Audio Gallery <SafeIcon icon={FiThumbsUp} className="ml-2" />
                </Link>
                <Link
                  to="/"
                  className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-bold hover:shadow-lg transition-all"
                >
                  Complete Journey <SafeIcon icon={FiCheckCircle} className="ml-2" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <PageLayout
      title="Quiz & Reflection"
      subtitle="Test your understanding and reflect on how Amharic grammar reveals cultural values and social dynamics."
      icon={FiCompass}
      color="text-purple-700"
      bgColor="bg-purple-50"
      prevLink="/audio"
      prevLabel="Audio Gallery"
      nextLink="/"
      nextLabel="Complete Learning"
    >
      <div className="space-y-8 fade-in">
        {/* Progress Overview */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-6 text-white shadow-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/30">
                <SafeIcon icon={FiCompass} className="text-3xl" />
              </div>
              <div>
                <h2 className="text-2xl font-bold mb-1">Knowledge Assessment</h2>
                <p className="text-purple-100">
                  {showResults
                    ? `You scored ${score} out of ${quizQuestions.length} questions correctly!`
                    : `Test your cultural and grammatical understanding`}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold">{formatTime(timeSpent)}</div>
              <div className="text-sm text-purple-200">Time Spent</div>
            </div>
          </div>
        </div>

        {/* Quiz Section */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-2xl font-bold text-gray-800">Cultural Knowledge Quiz</h3>
            {!showResults && (
              <span className="text-sm text-gray-500">
                Answered: {Object.keys(quizAnswers).length} of {quizQuestions.length}
              </span>
            )}
          </div>

          {/* Quiz Progress */}
          {!showResults && (
            <div className="bg-white rounded-xl p-4 border border-gray-200">
              <div className="flex justify-between items-center mb-2 text-sm text-gray-600">
                <span>Quiz Progress</span>
                <span>{Math.round((Object.keys(quizAnswers).length / quizQuestions.length) * 100)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 transition-all duration-500" style={{ width: `${(Object.keys(quizAnswers).length / quizQuestions.length) * 100}%` }} />
              </div>
            </div>
          )}

          {/* Quiz Questions */}
          <div className="space-y-6">
            {quizQuestions.map((question, index) => (
              <QuizCard key={question.id} question={question} index={index} total={quizQuestions.length} />
            ))}
          </div>

          {/* Quiz Actions */}
          {!showResults && (
            <div className="flex justify-center">
              <button
                onClick={submitQuiz}
                disabled={Object.keys(quizAnswers).length < quizQuestions.length}
                className={`px-8 py-4 rounded-xl font-bold text-lg transition-all ${Object.keys(quizAnswers).length === quizQuestions.length
                    ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:shadow-xl transform hover:scale-105'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  } `}
              >
                Submit Quiz <SafeIcon icon={FiCheckCircle} className="ml-2" />
              </button>
            </div>
          )}

          {showResults && score < quizQuestions.length * 0.8 && (
             <div className="text-center">
             <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-lg">
               <div className="mb-6">
                 <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center bg-yellow-500`}>
                   <SafeIcon icon={FiStar} className="text-3xl text-white" />
                 </div>
               </div>
               <h3 className="text-2xl font-bold text-gray-800 mb-2">
                 Good Effort!
               </h3>
               <p className="text-gray-600 mb-6">
                 You got {score} out of {quizQuestions.length} questions correct. Review the cultural notes and try again to become a master!
               </p>
               <button
                 onClick={resetQuiz}
                 className="px-6 py-3 bg-purple-500 text-white rounded-xl font-bold hover:bg-purple-600 transition-colors"
               >
                 <SafeIcon icon={FiRefreshCw} className="mr-2" /> Try Quiz Again
               </button>
             </div>
           </div>
          )}
        </div>

        {/* Reflection Section */}
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <SafeIcon icon={FiBook} className="text-2xl text-purple-600" />
            <h3 className="text-2xl font-bold text-gray-800">Cultural Reflection</h3>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {reflectionPrompts.map((reflection) => (
              <ReflectionCard key={reflection.id} reflection={reflection} />
            ))}
          </div>
        </div>

        {/* Key Takeaways */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-8 border border-green-200">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-green-100 rounded-full">
              <SafeIcon icon={FiTarget} className="text-green-600 text-2xl" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-green-900 mb-4">Key Takeaways</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <div>
                      <h4 className="font-bold text-green-800">Grammar Reflects Culture</h4>
                      <p className="text-green-700 text-sm">
                        Amharic pronouns and formality show Ethiopian values of respect, community, and social awareness.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <div>
                      <h4 className="font-bold text-green-800">Community Over Individual</h4>
                      <p className="text-green-700 text-sm">
                        Using "እኛ" (we) instead of "እኔ" (I) demonstrates humility and collective thinking.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <div>
                      <h4 className="font-bold text-green-800">Respect Through Language</h4>
                      <p className="text-green-700 text-sm">
                        Formal pronouns like "እርስዎ" maintain social harmony and show proper upbringing.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <div>
                      <h4 className="font-bold text-green-800">Context Matters</h4>
                      <p className="text-green-700 text-sm">
                        Different settings require different levels of formality and respectful address.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cultural Wisdom Quote */}
        <div className="bg-gradient-to-r from-purple-700 to-indigo-700 rounded-2xl p-8 text-white text-center shadow-xl">
          <div className="max-w-3xl mx-auto">
            <SafeIcon icon={FiMic} className="text-4xl text-yellow-400 mb-4" />
            <h3 className="text-2xl font-bold mb-4">Language as Cultural Wisdom</h3>
            <p className="text-purple-100 text-lg leading-relaxed mb-6">
              "In every greeting, in every pronoun choice, Ethiopian culture teaches us that we are never alone—we are always part of something greater than ourselves."
            </p>
            <div className="flex items-center justify-center gap-2 text-purple-200">
              <SafeIcon icon={FiHeart} />
              <span className="italic">Speak with respect, live in harmony</span>
              <SafeIcon icon={FiHeart} />
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Reflection;